import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('change', this.#handleSizeChange, { capture: true, signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#setupMobileGallery();
    this.#setupQuickAddFade();
  }

  #controller = new AbortController();
  #mobileTrack;
  #sizeSelectionRevision = 0;
  #quickAddFadeRevision = 0;
  #trackResizeObserver;
  #trackSettleTimer;
  #trackScrollFrame;
  #mobile = matchMedia('(max-width: 749px)');
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  #setupQuickAddFade() {
    if (!this.closest('quick-add-dialog') || !this.hasAttribute('data-sc-size-gallery')) return;
    const slides = this.querySelectorAll('slideshow-slides > slideshow-slide');
    if (!slides.length) return;
    this.setAttribute('data-sc-quick-add-fade', '');
    this.querySelector('slideshow-component')?.setAttribute('disabled', 'true');
    // Warm both size images as soon as the popup is inserted, not on first click.
    Array.from(slides).slice(0, 2).forEach((slide) => {
      const image = slide.querySelector('img');
      if (image) {
        image.sizes = '(min-width: 750px) 450px, calc(100vw - 56px)';
        image.loading = 'eager';
      }
    });
    this.#showQuickAddSlide(Number(this.dataset.scInitialSlide) || 0);
  }

  // Quick-add morphs an existing gallery when switching products/reopening.
  // Its connected callback need not run again, so reapply popup-only state.
  prepareQuickAddGallery() {
    this.#setupQuickAddFade();
  }

  #showQuickAddSlide(index) {
    const slides = this.querySelectorAll('slideshow-slides > slideshow-slide');
    const next = slides[index];
    if (!next) return;
    const revision = ++this.#quickAddFadeRevision;
    const reveal = () => {
      if (!this.isConnected || revision !== this.#quickAddFadeRevision) return;
      slides.forEach((slide) => {
        const active = slide === next;
        slide.classList.toggle('sc-quick-add-image-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
    };
    const image = next.querySelector('img');
    // Leave the current image visible until the new bitmap can be painted.
    if (image && !image.complete) image.decode().then(reveal).catch(() => {
      if (image.naturalWidth) reveal();
    });
    else reveal();
  }

  async #setupMobileGallery() {
    // Product-page gallery only; quick-add and recommendation media stay unchanged.
    if (!document.body.classList.contains('sc-product-page') ||
        !this.closest('.product-information__media') || this.closest('dialog')) return;
    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;
    const controls = this.querySelector('.media-gallery__mobile-controls');
    if (!controls || !this.slideshow || this.slideshow.slides.length < 2) return;

    const { signal } = this.#controller;
    this.querySelector('.sc-mobile-gallery')?.remove();
    this.#setupMobileTrack(signal);
    const initialIndex = Number(this.dataset.scInitialSlide);
    if (this.hasAttribute('data-sc-size-gallery') && initialIndex > 0) {
      requestAnimationFrame(() => {
        if (this.isConnected) this.slideshow?.select(initialIndex, undefined, { animate: false });
      });
    }

  }

  #setupMobileTrack(signal) {
    const slideshow = this.slideshow;
    const slides = slideshow?.slides;
    if (!slideshow || !slides?.length) return;
    const images = slides.map((slide) => slide.querySelector('.product-media__image'));
    if (images.some((image) => !image)) return;

    const count = images.length;
    const wrap = document.createElement('div');
    wrap.className = 'sc-mobile-gallery';
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', 'Product images');
    const track = document.createElement('div');
    track.className = 'sc-gallery-track';
    const controls = document.createElement('div');
    controls.className = 'sc-gallery-dots';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Choose product image');
    const indicator = document.createElement('span');
    indicator.className = 'sc-gallery-dot-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    controls.append(indicator);
    wrap.append(track, controls);
    slideshow.before(wrap);
    slideshow.classList.add('sc-gallery-native');

    // Keep neighbours at both ends for seamless wrapping and the next-image preview.
    const sequence = [count - 2, count - 1, ...images.map((_, i) => i), 0, 1];
    let position = 2 + Math.min(Number(this.dataset.scInitialSlide ?? slideshow.current), count - 1);
    let dragging = false;
    let pointerStart;
    const dots = [];
    const cards = sequence.map((index, physicalIndex) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'sc-gallery-card';
      card.setAttribute('aria-label', 'View product image ' + (index + 1));
      const isClone = physicalIndex < 2 || physicalIndex >= count + 2;
      card.tabIndex = -1;
      if (isClone) card.setAttribute('aria-hidden', 'true');
      const image = images[index].cloneNode(false);
      image.removeAttribute('id');
      image.removeAttribute('style');
      image.className = 'sc-gallery-card__image';
      image.loading = 'eager';
      image.draggable = false;
      card.append(image);
      track.append(card);
      card.addEventListener('click', (event) => {
        if (dragging) return;
        if (physicalIndex === position) this.zoom(index, event);
        else move(physicalIndex);
      }, { signal });
      return card;
    });
    const activeIndex = () => sequence[position];
    const update = (dotPosition = activeIndex()) => {
      cards.forEach((card, i) => {
        card.setAttribute('aria-current', String(i === position));
        card.tabIndex = i === activeIndex() + 2 ? 0 : -1;
      });
      dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === activeIndex())));
      indicator.style.transform = 'translateX(' + (dotPosition * 24) + 'px)';
    };
    const alignStart = (index, smooth) => {
      if (!this.#mobile.matches || !track.clientWidth) return;
      const card = cards[index];
      track.scrollTo({ left: card.offsetLeft,
        behavior: smooth && !this.#reducedMotion.matches ? 'smooth' : 'instant' });
    };
    const move = (index, smooth = true) => {
      position = Math.max(0, Math.min(cards.length - 1, index));
      alignStart(position, smooth);
      if (!smooth || this.#reducedMotion.matches) syncFromScroll();
    };
    const nearest = () => {
      return cards.reduce((best, card, i) =>
        Math.abs(card.offsetLeft - track.scrollLeft) <
        Math.abs(cards[best].offsetLeft - track.scrollLeft) ? i : best, 0);
    };
    // Follow the actual track position during touch scrolling, momentum and autoplay.
    // A second CSS transition here would make the indicator trail behind the finger.
    const syncFromScroll = () => {
      if (!track.clientWidth) return;
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (!step) return;
      const progress = Math.max(0, Math.min(cards.length - 1,
        (track.scrollLeft - cards[0].offsetLeft) / step));
      const left = Math.floor(progress);
      const right = Math.min(left + 1, cards.length - 1);
      position = Math.round(progress);
      update(sequence[left] + (sequence[right] - sequence[left]) * (progress - left));
    };
    const settle = () => {
      if (dragging || !track.clientWidth) return;
      position = nearest();
      if (position < 2 || position >= count + 2) {
        position = activeIndex() + 2;
        alignStart(position, false);
      }
      update();
    };
    images.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', 'Slide ' + (index + 1) + ' of ' + count);
      dot.addEventListener('click', () => {
        const candidates = sequence.map((value, i) => value === index ? i : -1).filter(i => i >= 0);
        move(candidates.reduce((best, i) => Math.abs(i - position) < Math.abs(best - position) ? i : best));
      }, { signal });
      dots.push(dot);
      controls.append(dot);
    });
    track.addEventListener('scroll', () => {
      if (!this.#trackScrollFrame) {
        this.#trackScrollFrame = requestAnimationFrame(() => {
          this.#trackScrollFrame = undefined;
          syncFromScroll();
        });
      }
      clearTimeout(this.#trackSettleTimer);
      this.#trackSettleTimer = setTimeout(settle, 120);
    }, { signal, passive: true });
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerStart = { x: event.clientX, scroll: track.scrollLeft };
    }, { signal });
    track.addEventListener('pointermove', (event) => {
      if (!pointerStart || Math.abs(event.clientX - pointerStart.x) < 5) return;
      dragging = true;
      track.setPointerCapture(event.pointerId);
      track.style.scrollSnapType = 'none';
      track.scrollLeft = pointerStart.scroll - (event.clientX - pointerStart.x);
    }, { signal });
    const finishDrag = () => {
      pointerStart = undefined;
      if (!dragging) return;
      const target = nearest();
      track.style.removeProperty('scroll-snap-type');
      move(target);
      setTimeout(() => { dragging = false; }, 0);
    };
    track.addEventListener('pointerup', finishDrag, { signal });
    track.addEventListener('pointercancel', finishDrag, { signal });
    this.#mobileTrack = {
      next: () => move(position + 1),
      select: (index, animate = false) => move(index + 2, animate),
    };
    this.#trackResizeObserver = new ResizeObserver(() => alignStart(position, false));
    this.#trackResizeObserver.observe(track);
    move(position, false);
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#quickAddFadeRevision++;
    this.#trackResizeObserver?.disconnect();
    clearTimeout(this.#trackSettleTimer);
    cancelAnimationFrame(this.#trackScrollFrame);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #selectSizeSlide(index) {
    this.dataset.scInitialSlide = String(index);
    if (this.hasAttribute('data-sc-quick-add-fade')) {
      this.#showQuickAddSlide(index);
      return;
    }
    this.#scrollDesktopMedia(index);
    this.#mobileTrack?.select(index, !this.#reducedMotion.matches);
    this.slideshow?.select(index, undefined, { animate: !this.#reducedMotion.matches });
  }

  #handleSizeChange = (event) => {
    if (!this.hasAttribute('data-sc-size-gallery')) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.checked) return;
    if (input.dataset.optionName?.toLowerCase() !== 'size') return;
    if (input.closest('variant-picker')?.dataset.productId !== this.dataset.productId) return;
    const size = input.value.trim().toLowerCase();
    const index = size.includes('9.6') ? 1 : size.includes('6.3') ? 0 : -1;
    if (index < 0) return;
    this.#sizeSelectionRevision++;
    this.#selectSizeSlide(index);
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const sizeRevision = this.#sizeSelectionRevision;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // A size change selects an existing slide instead of rebuilding the gallery.
        // Keeping the track preserves swipe position, thumbnail order and animation.
        if (this.hasAttribute('data-sc-size-gallery') &&
            newMediaGallery.dataset.productId === this.dataset.productId) {
            if (sizeRevision !== this.#sizeSelectionRevision) return;
          const index = Number(newMediaGallery.dataset.scInitialSlide);
          if (Number.isInteger(index) && index >= 0 && index < (this.slideshow?.slides.length ?? 0)) {
              if (String(index) !== this.dataset.scInitialSlide) this.#selectSizeSlide(index);
            return;
          }
        }
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.hasAttribute('data-sc-quick-add-fade')) {
      this.#showQuickAddSlide(event.detail.index);
      return;
    }
    this.#mobileTrack?.select(event.detail.index);
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  // The desktop grid retains catalog order, independent of variant-first slides.
  #gridMediaIndex(mediaId) {
    return Array.from(this.slideshow?.slides ?? []).findIndex((slide) =>
      slide.querySelector('[data-media-id]')?.dataset.mediaId === String(mediaId));
  }

  zoomGridMedia(mediaId, event) {
    const index = this.#gridMediaIndex(mediaId);
    if (index >= 0) this.zoom(index, event);
  }

  preloadGridMedia(mediaId) {
    const index = this.#gridMediaIndex(mediaId);
    if (index >= 0) this.preloadImage(index);
  }

  #scrollDesktopMedia(index) {
    if (!this.hasAttribute('data-sc-desktop-grid') || !matchMedia('(min-width: 990px)').matches) return;
    const mediaId = this.slideshow?.slides[index]?.querySelector('[data-media-id]')?.dataset.mediaId;
    const media = Array.from(this.querySelectorAll('.sc-desktop-media-grid > li'))
      .find((item) => item.dataset.scMediaId === mediaId);
    media?.scrollIntoView({ block: 'start', behavior: this.#reducedMotion.matches ? 'instant' : 'smooth' });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}





